import React, { useState, useMemo, useRef, useCallback, memo } from "react";
import {
  Box,
  Chip,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Modal,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { useStockPriceData } from "../hooks/useStockPriceData";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { getChartColors } from "../constants/chartColors";
import ErrorDisplay from "../../../common/ErrorDisplay";
import { modalBackdrop } from "../../../../theme";
import {
  compactSwitchSx,
  workspaceSwitchSx,
} from "../../../Dashboard/overviewPanel";
import { hapticSelection } from "../../../../lib/haptics";
import { getSymbolImageUrl } from "../../../../lib/symbolImage";
import { Quote } from "../hooks/useCompanyQuote";
import { Theme } from "@mui/material/styles";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatStockPrice } from "../../../../utils/stockPriceFormatter";
import { getExchangeTimezone } from "../../../../utils/exchangeTimezone";
import { useExchangeTradingMinutes } from "../hooks/useExchangeTradingMinutes";
import {
  evenlySpacedTickValues,
  evenlySpacedTimeTickValues,
} from "../utils/evenlySpacedTickValues";
import {
  TimeRange,
  ALL_TIME_RANGES,
  TIME_RANGE_CONFIG,
  RawPriceRecord,
} from "../types/stockChart";

// Measures the rendered pixel width of a Y-axis label so the chart's left
// margin can grow ONLY as much as a wide price label needs (e.g. BRK-A's
// "$738,610.00"). Normal-priced charts measure narrower than the base margin
// and are left untouched. A single reused canvas keeps this allocation-free.
let axisMeasureCanvas: HTMLCanvasElement | null = null;
const measureAxisLabelWidth = (text: string, fontSizePx: number): number => {
  if (typeof document === "undefined") return text.length * fontSizePx * 0.6;
  axisMeasureCanvas = axisMeasureCanvas ?? document.createElement("canvas");
  const ctx = axisMeasureCanvas.getContext("2d");
  if (!ctx) return text.length * fontSizePx * 0.6;
  ctx.font = `${fontSizePx}px Roboto, Helvetica, Arial, sans-serif`;
  return ctx.measureText(text).width;
};

// Flat UI Color System - Theme Aware
const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    // Primary Actions & Growth (similar in both themes)
    primary: "#22c55e", // Main green for positive actions
    primaryHover: "#16a34a", // Darker green for hover states

    // Negative/Decline States (similar in both themes)
    negative: "#ef4444", // Clear red for losses/negative data
    negativeHover: "#dc2626", // Darker red for hover

    // Neutral Interface (theme-dependent)
    background: isDark ? "#1a1a1a" : "#ffffff", // Dark grey-black or pure white
    surface: isDark ? "#212121" : "#f8fafc", // Slightly lighter dark or subtle gray
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0", // Subtle white lines or light gray
    borderSubtle: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9", // Very subtle borders

    // Typography (inverted for dark theme)
    textPrimary: isDark ? "#e4e6ea" : "#0f172a", // Light or dark text
    textSecondary: isDark ? "#9ca3af" : "#64748b", // Medium contrast text
    textMuted: isDark ? "#6b7280" : "#94a3b8", // Low contrast text

    // Accent/Information (slightly adjusted for dark)
    accent: isDark ? "#60a5fa" : "#3b82f6", // Brighter blue in dark mode
    warning: isDark ? "#fbbf24" : "#f59e0b", // Brighter orange in dark mode
  };
};

dayjs.extend(utc);
dayjs.extend(timezone);

// Flat UI Typography System
const flatUITypography = {
  // Body - General content
  body: {
    fontSize: "1rem", // 16px
    fontWeight: 400, // Regular
    lineHeight: 1.5,
  },

  // Caption - Small metrics, timestamps
  caption: {
    fontSize: "0.875rem", // 14px
    fontWeight: 400,
    lineHeight: 1.4,
  },

  // Label - Form labels, tiny text
  label: {
    fontSize: "0.75rem", // 12px
    fontWeight: 500, // Medium
    lineHeight: 1.3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
};

interface StockPriceChartProps {
  ticker: string;
  company?: Quote;
  height?: number | string;
  isModal?: boolean;
  onOpenModal?: () => void;
  tradingDate?: string;
  renderCard?: boolean;
  showFullscreen?: boolean;
  defaultTimeRange?: TimeRange;
  showTimeRangeSelector?: boolean;
  /** Restrict the selector and the fetches to these ranges (all by default). */
  ranges?: readonly TimeRange[];
  /** An embedded chart: no period-change header and tighter margins. */
  compact?: boolean;
  /** A range chosen outside the chart (keep its own selector hidden). */
  timeRange?: TimeRange;
}

interface ChartDataPoint {
  x: string;
  y: number;
  originalDate: string;
  displayX: string; // Formatted display value for axis labels
  localTime: string;
  etTime: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  [key: string]: any;
}

interface ProcessedRangeData {
  chartData: { id: string; data: ChartDataPoint[] }[];
  yAxisMin: number;
  yAxisMax: number;
}

// FMP returns a 1-minute candle only for minutes that actually traded, so an
// illiquid name (e.g. BRK-A) comes back with irregular gaps. On the index-based
// "point" x-scale that makes equal-TIME axis marks land at unequal pixels, so
// the 1D labels read unevenly (a snapped "15:25" sitting next to "16:00").
// Resampling onto a continuous 1-minute grid — carrying the last close forward
// across gaps — restores index == time, so evenlySpacedTimeTickValues lands
// clean, evenly-spaced, round-time labels. Only internal gaps are filled: the
// series still starts at the first trade and ends at the last.
const fillIntradayMinuteGrid = (
  points: ChartDataPoint[],
  exchangeTimezone: string,
  userTimezone: string,
  xAxisFormat: string
): ChartDataPoint[] => {
  if (points.length < 2) return points;

  const minuteKey = (date: string) =>
    dayjs.tz(date, exchangeTimezone).format("YYYY-MM-DD HH:mm");
  const byMinute = new Map<string, ChartDataPoint>();
  points.forEach((p) => byMinute.set(minuteKey(p.originalDate), p));

  const start = dayjs
    .tz(points[0].originalDate, exchangeTimezone)
    .startOf("minute");
  const end = dayjs
    .tz(points[points.length - 1].originalDate, exchangeTimezone)
    .startOf("minute");

  // An unparseable date makes `!t.isAfter(end)` permanently true (infinite
  // loop → tab hang) and a misparsed-but-valid one makes the span absurd —
  // bail to the raw points in both cases.
  if (!start.isValid() || !end.isValid() || end.diff(start, "minute") > 50000) return points;

  const filled: ChartDataPoint[] = [];
  let last = points[0];
  for (let t = start; !t.isAfter(end); t = t.add(1, "minute")) {
    const real = byMinute.get(t.format("YYYY-MM-DD HH:mm"));
    if (real) {
      filled.push(real);
      last = real;
      continue;
    }
    // No trade this minute — carry the last close forward as a flat candle.
    const localTime = t.tz(userTimezone);
    const isoMinute = t.format("YYYY-MM-DD HH:mm:ss");
    filled.push({
      x: isoMinute,
      y: last.close,
      originalDate: isoMinute,
      displayX: localTime.format(xAxisFormat),
      localTime: localTime.format("HH:mm"),
      etTime: t.format("HH:mm"),
      displayTime: localTime.format("HH:mm"),
      open: last.close,
      high: last.close,
      low: last.close,
      close: last.close,
      volume: 0,
    });
  }
  return filled;
};

// Phones: the ranges share the card's width evenly.
const mobileRangeSx = {
  width: "100%",
  "& .MuiToggleButton-root": { flex: 1, minWidth: 0, px: 0 },
};

// Memoized Time Range Selector - prevents re-renders when parent re-renders
interface TimeRangeSelectorProps {
  selectedTimeRange: TimeRange;
  onSelectRange: (range: TimeRange) => void;
  isMobile?: boolean;
  ranges?: readonly TimeRange[];
}

const TimeRangeSelector = memo<TimeRangeSelectorProps>(
  ({
    selectedTimeRange,
    onSelectRange,
    isMobile = false,
    ranges = ALL_TIME_RANGES,
  }) => (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        justifyContent: "center",
        flexWrap: isMobile ? "nowrap" : "wrap",
      }}
    >
      <ToggleButtonGroup
        value={selectedTimeRange}
        exclusive
        size="small"
        aria-label="Price range"
        onChange={(_, range: TimeRange | null) => {
          if (range === null || range === selectedTimeRange) return;
          hapticSelection();
          onSelectRange(range);
        }}
        // An array, so the phone styles merge into the shared button styles
        // rather than replacing them.
        sx={[
          workspaceSwitchSx,
          isMobile && compactSwitchSx,
          isMobile && mobileRangeSx,
        ]}
      >
        {ranges.map((range) => (
          <ToggleButton key={range} value={range}>
            {range}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  )
);

TimeRangeSelector.displayName = "TimeRangeSelector";

const StockPriceChart: React.FC<StockPriceChartProps> = ({
  ticker,
  company,
  height = 400,
  isModal = false,
  onOpenModal,
  tradingDate,
  renderCard = true,
  showFullscreen = false,
  defaultTimeRange = "1D",
  showTimeRangeSelector = false,
  ranges,
  compact = false,
  timeRange,
}) => {
  const theme = useTheme();
  const flatUIColors = useMemo(() => getFlatUIColors(theme), [theme]);
  const { currency, exchange } = useStockCurrency();
  const exchangeTimezone = getExchangeTimezone(exchange);
  const { openMinutes, closeMinutes } = useExchangeTradingMinutes(exchange);
  const [modalOpen, setModalOpen] = useState(false);
  const [internalTimeRange, setSelectedTimeRange] =
    useState<TimeRange>(defaultTimeRange);
  const selectedTimeRange = timeRange ?? internalTimeRange;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  // Custom breakpoint at 1080px to include Surface Pro (912px) and iPad Pro landscape
  // but exclude laptops (typically 1280px+) - matches ViewTicker breakpoint
  const isTabletOrSmaller = useMediaQuery("(max-width: 1080px)");

  // Unique ID for tooltip group - allows direct DOM manipulation without React re-renders
  const tooltipGroupId = useMemo(
    () => `tooltip-${ticker}-${Math.random().toString(36).slice(2, 11)}`,
    [ticker]
  );

  // Ref to persist last valid slice - prevents flickering during slice transitions
  const lastSliceRef = useRef<any>(null);

  // Clear the slice ref when time range changes to avoid stale data
  const prevTimeRangeRef = useRef<TimeRange>(selectedTimeRange);
  if (prevTimeRangeRef.current !== selectedTimeRange) {
    lastSliceRef.current = null;
    prevTimeRangeRef.current = selectedTimeRange;
  }

  // Unified hook for all time ranges - fetches data for all ranges in parallel
  const priceData = useStockPriceData(ticker, ranges);

  // Get the current selection's loading/error state
  const currentRangeData = priceData[selectedTimeRange];
  const { isLoading, isError, error, metadata } = currentRangeData;
  const fallbackUsed = metadata?.fallbackUsed;
  const actualDate = metadata?.actualDate ?? metadata?.actualFrom;

  // Get config for current time range
  const currentConfig = TIME_RANGE_CONFIG[selectedTimeRange];

  const colors = useMemo(
    () => getChartColors(theme.palette.mode === "dark"),
    [theme.palette.mode]
  );

  // Helper function to process raw data into chart-ready format
  const processRawData = useMemo(() => {
    const userTimezone = dayjs.tz.guess();

    return (
      rawData: RawPriceRecord[] | undefined,
      config: typeof currentConfig
    ): ProcessedRangeData => {
      if (!rawData || rawData.length === 0) {
        return { chartData: [], yAxisMin: 0, yAxisMax: 0 };
      }

      const xAxisFormat = config.xAxisFormat;
      const resolution = config.resolution;
      const isIntraday = resolution.startsWith("intraday");

      // Numeric-timestamp sort (Date.parse) instead of two dayjs constructions
      // per comparison; copy first so the react-query cache array isn't mutated
      const sorted = [...rawData].sort(
        (a: RawPriceRecord, b: RawPriceRecord) =>
          Date.parse(a.date) - Date.parse(b.date)
      );

      // Single pass with ONE dayjs.tz conversion per point, reused for both
      // the market-hours filter and the display values
      const processedData: ChartDataPoint[] = [];
      for (const item of sorted) {
        const exchangeTime = dayjs.tz(item.date, exchangeTimezone);

        if (isIntraday) {
          const timeInMinutes =
            exchangeTime.hour() * 60 + exchangeTime.minute();
          if (timeInMinutes < openMinutes || timeInMinutes > closeMinutes) {
            continue;
          }
        }

        const localTime = exchangeTime.tz(userTimezone);

        const close =
          isFinite(item.close) && !isNaN(item.close) ? item.close : 0;
        const open =
          isFinite(item.open) && !isNaN(item.open) ? item.open : close;
        const high =
          isFinite(item.high) && !isNaN(item.high) ? item.high : close;
        const low = isFinite(item.low) && !isNaN(item.low) ? item.low : close;
        const volume =
          isFinite(item.volume) && !isNaN(item.volume) ? item.volume : 0;

        if (!isFinite(close) || isNaN(close)) continue;

        // Use original date as x-value to ensure uniqueness
        // This prevents duplicate x-values when data spans multiple weeks
        // (e.g., two Tuesdays in a 1W view would both be "Tue 08:00" with ddd HH:mm format)
        const localHHmm = localTime.format("HH:mm");
        processedData.push({
          x: item.date,
          y: close,
          originalDate: item.date,
          displayX: localTime.format(xAxisFormat), // Formatted display value for axis labels
          localTime: localHHmm,
          etTime: exchangeTime.format("HH:mm"),
          displayTime: localHHmm,
          open,
          high,
          low,
          close,
          volume,
        });
      }

      // 1D only: resample onto a continuous 1-minute grid so the time axis is
      // uniformly sampled and the labels space evenly (see fillIntradayMinuteGrid).
      const griddedData =
        resolution === "intraday-1min"
          ? fillIntradayMinuteGrid(
              processedData,
              exchangeTimezone,
              userTimezone,
              xAxisFormat
            )
          : processedData;

      const prices = griddedData
        .map((item: ChartDataPoint) => item.close)
        .filter((price: number) => !isNaN(price) && isFinite(price));
      const dataMin = prices.length > 0 ? Math.min(...prices) : 0;
      const dataMax = prices.length > 0 ? Math.max(...prices) : 0;
      const range = dataMax - dataMin;

      let yAxisMin = 0;
      if (range > 0 && isFinite(dataMin) && !isNaN(dataMin)) {
        yAxisMin = dataMin - range * 0.05;
      } else if (isFinite(dataMin) && !isNaN(dataMin)) {
        yAxisMin = dataMin - 0.01;
      }

      if (!isFinite(yAxisMin) || isNaN(yAxisMin)) {
        yAxisMin = 0;
      }

      return {
        chartData: [{ id: "price", data: griddedData }],
        yAxisMin,
        yAxisMax: dataMax,
      };
    };
  }, [exchangeTimezone, openMinutes, closeMinutes]);

  // Lazily process ONLY the selected time range. Results are cached per range,
  // keyed on the raw data reference (stable via react-query structural sharing),
  // so re-renders with unchanged data do zero work and flipping back to an
  // already-processed range is instant.
  const processedRangeCacheRef = useRef(
    new Map<
      TimeRange,
      { source: RawPriceRecord[] | undefined; result: ProcessedRangeData }
    >()
  );

  // Invalidate the cache if the processing inputs change (exchange timezone / trading hours)
  const prevProcessRawDataRef = useRef(processRawData);
  if (prevProcessRawDataRef.current !== processRawData) {
    processedRangeCacheRef.current.clear();
    prevProcessRawDataRef.current = processRawData;
  }

  const { chartData, yAxisMin, yAxisMax } = useMemo(() => {
    const rangeData = priceData[selectedTimeRange].data;
    const cached = processedRangeCacheRef.current.get(selectedTimeRange);
    if (cached && cached.source === rangeData) {
      return cached.result;
    }

    const result = processRawData(
      rangeData,
      TIME_RANGE_CONFIG[selectedTimeRange]
    );
    processedRangeCacheRef.current.set(selectedTimeRange, {
      source: rangeData,
      result,
    });
    return result;
  }, [priceData, selectedTimeRange, processRawData]);

  // Responsive left margin: widen only when the widest Y-axis price label would
  // overflow the base margin (e.g. BRK-A "$738,610.00"), otherwise keep the base
  // so normal-priced charts are unaffected and the plot uses the most room.
  const leftAxisMargin = useMemo(() => {
    const baseLeft = isTabletOrSmaller ? 50 : 70;
    const fontSize = isMobile ? 10 : 13;
    const tickPadding = isMobile ? 8 : 12;
    const labels = [yAxisMin, yAxisMax]
      .filter((v) => typeof v === "number" && isFinite(v))
      .map((v) => formatStockPrice({ price: v, currency }));
    if (labels.length === 0) return baseLeft;
    const widest = Math.max(
      ...labels.map((label) => measureAxisLabelWidth(label, fontSize))
    );
    // Label text ends `tickPadding` left of the axis; +4 keeps it off the edge.
    return Math.max(baseLeft, Math.ceil(widest) + tickPadding + 4);
  }, [yAxisMin, yAxisMax, currency, isMobile, isTabletOrSmaller]);

  // Calculate period change (first price to last price) for the selected time range
  const periodChange = useMemo(() => {
    const data = chartData[0]?.data;
    if (!data || data.length < 2) return null;

    const firstPrice = data[0].close;
    const lastPrice = data[data.length - 1].close;

    if (!firstPrice || !isFinite(firstPrice) || firstPrice === 0) return null;

    const change = lastPrice - firstPrice;
    const changePercent = (change / firstPrice) * 100;

    return {
      change,
      changePercent,
      isPositive: changePercent >= 0,
    };
  }, [chartData]);

  // Memoized axis format function - prevents recreating on every render
  // x-values are original date strings (ISO format) for uniqueness,
  // so we parse them in exchange timezone and convert to user timezone for display
  const axisBottomFormat = useMemo(() => {
    const userTimezone = dayjs.tz.guess();

    // Helper to convert exchange time to user's local timezone
    const toLocalTime = (value: string) => {
      const exchangeTime = dayjs.tz(value, exchangeTimezone);
      return exchangeTime.tz(userTimezone);
    };

    if (selectedTimeRange === "1D") {
      return (value: string) => {
        const localTime = toLocalTime(value);
        return localTime.isValid() ? localTime.format("HH:mm") : value;
      };
    }
    if (selectedTimeRange === "1W") {
      return (value: string) => {
        const localTime = toLocalTime(value);
        return localTime.isValid() ? localTime.format("ddd") : value;
      };
    }
    if (selectedTimeRange === "1M" || selectedTimeRange === "3M") {
      return (value: string) => {
        const localTime = toLocalTime(value);
        return localTime.isValid() ? localTime.format("MMM D") : value;
      };
    }
    if (selectedTimeRange === "6M") {
      return (value: string) => {
        const localTime = toLocalTime(value);
        return localTime.isValid() ? localTime.format("MMM") : value;
      };
    }
    // YTD: format based on current resolution (determined by days since Jan 1)
    if (selectedTimeRange === "YTD") {
      const now = dayjs();
      const yearStart = now.startOf("year");
      const daysSinceYearStart = now.diff(yearStart, "day");

      if (daysSinceYearStart <= 7) {
        // 15-min resolution: show day + time
        return (value: string) => {
          const localTime = toLocalTime(value);
          return localTime.isValid() ? localTime.format("ddd HH:mm") : value;
        };
      }
      if (daysSinceYearStart <= 31) {
        // 30-min resolution: show date + time
        return (value: string) => {
          const localTime = toLocalTime(value);
          return localTime.isValid() ? localTime.format("MMM D") : value;
        };
      }
      if (daysSinceYearStart <= 180) {
        // 4-hour resolution: show month
        return (value: string) => {
          const localTime = toLocalTime(value);
          return localTime.isValid() ? localTime.format("MMM") : value;
        };
      }
      // Daily: show month + year
      return (value: string) => {
        const localTime = toLocalTime(value);
        if (localTime.isValid()) {
          return `${localTime.format("MMM")} '${localTime.format("YY")}`;
        }
        return value;
      };
    }
    // 1Y, 5Y, 10Y all use daily data with ISO date format
    if (
      selectedTimeRange === "1Y" ||
      selectedTimeRange === "5Y" ||
      selectedTimeRange === "10Y"
    ) {
      return (value: string) => {
        const localTime = toLocalTime(value);
        if (localTime.isValid()) {
          return `${localTime.format("MMM")} '${localTime.format("YY")}`;
        }
        return value;
      };
    }
    return undefined;
  }, [selectedTimeRange, exchangeTimezone]);

  // Evenly-spaced (never-overlapping) x-axis labels via the shared index-based
  // utility — same approach as the Financials charts. Equal index spacing keeps
  // labels uniform on any width and time horizon, even with irregular/after-hours
  // sampling that previously clustered the 1D ticks.
  //
  // 1D shows clock times, so we use the time-aware variant that snaps the evenly
  // spaced ticks onto :00/:30 boundaries (in the user's local timezone, matching
  // axisBottomFormat) for consistent-looking times. Other ranges show dates and
  // stay on the plain index-based variant.
  const axisBottomTickValues = useMemo(() => {
    const xValues = chartData?.[0]?.data.map((d) => String(d.x)) ?? [];
    if (xValues.length === 0) return undefined;

    if (selectedTimeRange === "1D") {
      const userTimezone = dayjs.tz.guess();
      const minutesOfDay = (value: string) => {
        const local = dayjs.tz(value, exchangeTimezone).tz(userTimezone);
        return local.isValid() ? local.hour() * 60 + local.minute() : 0;
      };
      return evenlySpacedTimeTickValues(xValues, minutesOfDay, isMobile);
    }

    // The compact chart is a third of a desktop width: four labels fit.
    return evenlySpacedTickValues(xValues, isMobile, compact ? 4 : undefined);
  }, [chartData, selectedTimeRange, isMobile, exchangeTimezone, compact]);

  // Memoized callback for time range selection - stable reference
  const handleSelectRange = useCallback((range: TimeRange) => {
    setSelectedTimeRange(range);
  }, []);

  // Memoized custom tooltip layer - prevents recreating function on every render
  const tooltipLayer = useCallback(
    ({ currentSlice, innerHeight, innerWidth }: any) => {
      // Persist last valid slice to prevent flickering during transitions
      if (currentSlice) {
        lastSliceRef.current = currentSlice;
      }

      // Use current slice or fall back to last valid slice
      const sliceToRender = currentSlice || lastSliceRef.current;

      // Always render the <g> element so it exists in DOM for opacity toggling
      // Only render content when we have slice data
      const point = sliceToRender?.points?.[0];
      const hasData = point && point.data;

      // Default values when no data
      let x = 0;
      let labelX = 0;
      const isDaily = currentConfig.resolution === "daily";
      // Tablet/Mobile: shorter format (no AM/PM) to prevent wrapping
      const dateFormat = isDaily
        ? isTabletOrSmaller
          ? "MMM D, YY"
          : "MMM D, YYYY"
        : isTabletOrSmaller
          ? "MMM D, H:mm"
          : "MMM D, h:mm A";

      // Convert exchange time to user's local timezone for tooltip display
      const userTimezone = dayjs.tz.guess();
      const formatTooltipDate = (originalDate: string) => {
        const exchangeTime = dayjs.tz(originalDate, exchangeTimezone);
        return exchangeTime.tz(userTimezone).format(dateFormat);
      };
      // Tablet/Mobile: compact tooltip to fit in smaller margins
      // Increased widths to accommodate comma-formatted prices (e.g., "$1,030.00")
      const labelWidth = isTabletOrSmaller ? (isDaily ? 160 : 175) : isDaily ? 215 : 230;
      const labelHalfWidth = labelWidth / 2;
      const labelHeight = isTabletOrSmaller ? 28 : 36;

      if (hasData) {
        x = point.x;
        labelX = Math.max(
          labelHalfWidth,
          Math.min(x, innerWidth - labelHalfWidth)
        );
      }

      return (
        <g
          id={tooltipGroupId}
          style={{ opacity: 0, transition: "opacity 0.1s ease-out" }}
        >
          {hasData && (
            <>
              {/* Vertical line */}
              <line
                x1={x}
                x2={x}
                y1={0}
                y2={innerHeight}
                stroke={flatUIColors.textMuted}
                strokeWidth={1}
                strokeDasharray="4 4"
              />
              {/* Top label using foreignObject for proper flexbox centering */}
              <foreignObject
                x={labelX - labelHalfWidth}
                y={-labelHeight - 4}
                width={labelWidth}
                height={labelHeight}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "center",
                    gap: isTabletOrSmaller ? 0.5 : 0.75,
                    backgroundColor: flatUIColors.surface,
                    border: `1px solid ${flatUIColors.border}`,
                    borderRadius: isTabletOrSmaller ? 1 : 1.5,
                    px: isTabletOrSmaller ? 1 : 1.5,
                    pt: isTabletOrSmaller ? "7px" : "11px",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: isTabletOrSmaller ? "0.6875rem" : "0.8125rem",
                      fontWeight: 500,
                      color: flatUIColors.textSecondary,
                      lineHeight: 1,
                    }}
                  >
                    {formatTooltipDate(point.data.originalDate)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: isTabletOrSmaller ? "0.6875rem" : "0.8125rem",
                      color: flatUIColors.textMuted,
                      lineHeight: 1,
                    }}
                  >
                    ·
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: isTabletOrSmaller ? "0.75rem" : "0.9375rem",
                      fontWeight: 600,
                      color: "#22c55e",
                      lineHeight: 1,
                    }}
                  >
                    {formatStockPrice({ price: (point.data as ChartDataPoint).close, currency })}
                  </Typography>
                </Box>
              </foreignObject>
            </>
          )}
        </g>
      );
    },
    [tooltipGroupId, currentConfig.resolution, flatUIColors, isTabletOrSmaller, currency, exchangeTimezone]
  );

  // Memoized layers array - only changes when tooltipLayer changes
  const chartLayers = useMemo(
    () =>
      [
        "grid",
        "markers",
        "axes",
        "areas",
        "lines",
        "points",
        "slices",
        "legends",
        tooltipLayer,
      ] as const,
    [tooltipLayer]
  );

  if (isLoading) {
    return (
      <Card
        elevation={0}
        sx={{ backgroundColor: theme.palette.background.paper }}
      >
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={height}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isError && error) {
    return (
      <Card
        elevation={0}
        sx={{ backgroundColor: theme.palette.background.paper }}
      >
        <CardContent>
          <ErrorDisplay
            error={error}
            onRetry={() => window.location.reload()}
            fullWidth
          />
        </CardContent>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0 || chartData[0].data.length === 0) {
    return (
      <Card
        elevation={0}
        sx={{ backgroundColor: theme.palette.background.paper }}
      >
        <CardContent>
          <Alert severity="info">
            No price data available for the selected trading day.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const chartContent = (
    <Box
      sx={{
        // Use the exact height prop - parent controls the container size
        height: height === "100%" ? "100%" : height,
        display: "flex",
        flexDirection: "column",
        ...(height === "100%" && { flex: 1 }),
      }}
    >
      {/* Desktop only: Simplified Header - Logo and Company Name Centered with Fullscreen in Top Right */}
      {/* Hidden on tablet and mobile to save vertical space */}
      {!isTabletOrSmaller && !compact && (
        <Box
          sx={{
            position: "relative",
            mb: height === "100%" ? 1 : 2,
            // Identity sits in the centre column; the period change lives in
            // the right column so it never nudges the name off centre.
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
          }}
        >
          {company && (
            <Box
              sx={{
                gridColumn: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <Box sx={{ position: "relative", width: 28, height: 28 }}>
                <Box
                  component="img"
                  src={getSymbolImageUrl(company.symbol)}
                  sx={{
                    width: 28,
                    height: 28,
                    objectFit: "contain",
                  }}
                  alt={`${company.symbol} logo`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = "flex";
                    }
                  }}
                />
                <Box
                  sx={{
                    display: "none",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: 0.75,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(34, 197, 94, 0.15)"
                        : "rgba(34, 197, 94, 0.1)",
                    border:
                      theme.palette.mode === "dark"
                        ? "1px solid rgba(34, 197, 94, 0.3)"
                        : "1px solid rgba(34, 197, 94, 0.2)",
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    color: "#22c55e",
                  }}
                >
                  {company.name?.charAt(0)?.toUpperCase() ??
                    company.symbol?.charAt(0)?.toUpperCase()}
                </Box>
              </Box>
              <Typography
                sx={{
                  ...flatUITypography.body,
                  fontWeight: 600,
                  color: flatUIColors.textPrimary,
                  lineHeight: 1,
                  fontSize: "0.9375rem",
                }}
              >
                {company.name}
              </Typography>

            </Box>
          )}

          {/* Period change, hidden for 1D since the page header shows the daily move */}
          {periodChange && selectedTimeRange !== "1D" && (
            <Typography
              sx={{
                gridColumn: 3,
                // Beside the name when there is one; otherwise the period
                // return sits at the chart's top-right rather than mid-width.
                justifySelf: company ? "start" : "end",
                pl: company ? 1.5 : 0,
                fontSize: "0.8125rem",
                fontWeight: 600,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                color: periodChange.isPositive ? "#22c55e" : "#ef4444",
              }}
            >
              {periodChange.isPositive ? "+" : ""}
              {Math.abs(periodChange.changePercent) >= 1000
                ? periodChange.changePercent.toLocaleString("en-US", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
                : periodChange.changePercent.toFixed(1)}
              %
            </Typography>
          )}

          {/* Fullscreen Button - Top Right, centred on the title row */}
          {showFullscreen && !isModal && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                right: 0,
                transform: "translateY(-50%)",
              }}
            >
              <IconButton
                onClick={onOpenModal || (() => setModalOpen(true))}
                size="small"
                sx={{
                  color: "primary.main",
                }}
              >
                <FullscreenIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          // Use flex: 1 to fill remaining space after controls
          flex: 1,
          minHeight: 0, // Allow shrinking in flex container
          ...(isModal && { height: "100%" }),
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.015)",
          borderRadius: 1,
        }}
        onMouseEnter={() => {
          // Direct DOM manipulation - no React re-render needed
          const el = document.getElementById(tooltipGroupId);
          if (el) el.style.opacity = "1";
        }}
        onMouseLeave={() => {
          // Direct DOM manipulation - no React re-render needed
          const el = document.getElementById(tooltipGroupId);
          if (el) el.style.opacity = "0";
          lastSliceRef.current = null;
        }}
      >
        <ResponsiveLine
          data={chartData}
          animate={false}
          margin={
            compact
              ? // 40 on top still clears the hover label (36px + 4px gap); 40
                // below keeps the date labels off the plot's bottom edge.
                { top: 40, right: 28, bottom: 40, left: leftAxisMargin }
              : {
                  // Tablet/Mobile: smaller top margin since no header, but enough for tooltip
                  top: height === "100%" ? 48 : isTabletOrSmaller ? 36 : 50,
                  // Right margin to prevent last x-axis label cutoff
                  right: isTabletOrSmaller ? 25 : 35,
                  // Tablet/Mobile: compact bottom margin with slight padding for controls
                  bottom: height === "100%" ? 50 : isTabletOrSmaller ? 36 : 70,
                  // Grows only when a wide price label needs it (see leftAxisMargin).
                  left: leftAxisMargin,
                }
          }
          xScale={{ type: "point" }}
          yScale={{
            type: "linear",
            min: "auto",
            max: "auto",
            stacked: false,
            reverse: false,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: isMobile ? 6 : 12,
            tickRotation: 0, // Horizontal labels on all devices
            legendOffset: 60,
            legendPosition: "middle",
            format: axisBottomFormat,
            tickValues: axisBottomTickValues,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: isMobile ? 8 : 12,
            tickRotation: 0,
            legendOffset: -60,
            legendPosition: "middle",
            format: (value) => formatStockPrice({ price: value, currency }),
            tickValues: isMobile ? 4 : 5, // Show fewer ticks on mobile to reduce crowding
          }}
          pointSize={0}
          pointColor={{ theme: "background" }}
          pointBorderWidth={0}
          pointBorderColor={{ from: "serieColor" }}
          pointLabelYOffset={-12}
          useMesh={false}
          colors={[colors.positive]}
          lineWidth={2.5}
          enableGridX={!isMobile}
          gridXValues={axisBottomTickValues}
          enableGridY={true}
          gridYValues={5}
          enableArea={true}
          areaOpacity={0.4}
          areaBaselineValue={yAxisMin}
          isInteractive={true}
          enableSlices="x"
          sliceTooltip={() => null}
          layers={chartLayers}
          theme={{
            background: "transparent",
            text: {
              fontSize: isMobile ? 10 : 12,
              fill: flatUIColors.textMuted,
              outlineWidth: 0,
              outlineColor: "transparent",
            },
            axis: {
              domain: {
                line: {
                  stroke: "transparent",
                  strokeWidth: 0,
                },
              },
              legend: {
                text: {
                  fontSize: isMobile ? 11 : 14,
                  fill: flatUIColors.textSecondary,
                  fontWeight: 600,
                },
              },
              ticks: {
                line: {
                  stroke: "transparent",
                  strokeWidth: 0,
                },
                text: {
                  fontSize: isMobile ? 10 : 11,
                  fill: flatUIColors.textPrimary,
                  fontWeight: 600,
                },
              },
            },
            grid: {
              line: {
                stroke: theme.palette.divider,
                strokeWidth: 1,
                strokeOpacity: 0.2,
              },
            },
          }}
          defs={[
            {
              id: "intradayGradient",
              type: "linearGradient",
              colors: [
                { offset: 0, color: colors.positive, opacity: 0.5 },
                { offset: 50, color: colors.positive, opacity: 0.25 },
                { offset: 100, color: colors.positive, opacity: 0 },
              ],
            },
          ]}
          fill={[{ match: "*", id: "intradayGradient" }]}
        />
      </Box>

      {/* Time Range Selector - Below Chart */}
      {showTimeRangeSelector && (
        <Box
          sx={{
            flexShrink: 0, // Don't compress controls
            // Same breathing room above the switch as the card leaves below it.
            pt: compact ? 0.75 : 1.5,
            pb: isMobile ? 0.5 : 0,
          }}
        >
          <TimeRangeSelector
            selectedTimeRange={selectedTimeRange}
            onSelectRange={handleSelectRange}
            isMobile={isMobile}
            ranges={ranges}
          />
        </Box>
      )}

      {fallbackUsed && actualDate && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Chip
            label="Showing data from the last trading day"
            size="small"
            sx={{
              fontSize: "0.75rem",
              height: 24,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 193, 7, 0.15)"
                  : "rgba(255, 193, 7, 0.1)",
              color: theme.palette.mode === "dark" ? "#FFC107" : "#E65100",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 193, 7, 0.3)"
                  : "rgba(255, 193, 7, 0.4)"
              }`,
            }}
          />
        </Box>
      )}
    </Box>
  );

  if (!renderCard) {
    return (
      <>
        {chartContent}

        {/* Internal Fullscreen Modal */}
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
          slotProps={{
            backdrop: {
              sx: modalBackdrop,
            },
          }}
        >
          <Box
            sx={{
              width: "90vw",
              height: "90vh",
              position: "relative",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 4,
            }}
          >
            <IconButton
              onClick={() => setModalOpen(false)}
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 1000 }}
            >
              <CloseIcon />
            </IconButton>

            <Box
              sx={{
                height: "100%",
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <StockPriceChart
                ticker={ticker}
                company={company}
                height="100%"
                isModal={true}
                tradingDate={tradingDate}
                renderCard={false}
                showTimeRangeSelector={showTimeRangeSelector}
                defaultTimeRange={selectedTimeRange}
              />
            </Box>
          </Box>
        </Modal>
      </>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        ...(isModal && {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      <CardContent
        sx={{
          ...(isModal && {
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }),
        }}
      >
        {chartContent}
      </CardContent>

      {/* Internal Fullscreen Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
        slotProps={{
          backdrop: {
            sx: modalBackdrop,
          },
        }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "90vh",
            position: "relative",
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 4,
          }}
        >
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1000 }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              height: "100%",
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <StockPriceChart
              ticker={ticker}
              company={company}
              height="100%"
              isModal={true}
              tradingDate={tradingDate}
              renderCard={false}
              showTimeRangeSelector={showTimeRangeSelector}
              defaultTimeRange={selectedTimeRange}
            />
          </Box>
        </Box>
      </Modal>
    </Card>
  );
};

// Modal wrapper component
interface StockPriceChartModalProps {
  ticker: string;
  company?: Quote;
  open: boolean;
  onClose: () => void;
  tradingDate?: string;
  showTimeRangeSelector?: boolean;
  defaultTimeRange?: TimeRange;
}

export const StockPriceChartModal: React.FC<StockPriceChartModalProps> = ({
  ticker,
  company,
  open,
  onClose,
  tradingDate,
  showTimeRangeSelector = false,
  defaultTimeRange = "1D",
}) => {
  const theme = useTheme();

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
      slotProps={{
        backdrop: {
          sx: modalBackdrop,
        },
      }}
    >
      <Box
        sx={{
          width: "90vw",
          height: "90vh",
          position: "relative",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          borderRadius: 4,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1000,
          }}
        >
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            height: "100%",
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <StockPriceChart
            ticker={ticker}
            company={company}
            height="100%"
            isModal={true}
            tradingDate={tradingDate}
            showTimeRangeSelector={showTimeRangeSelector}
            defaultTimeRange={defaultTimeRange}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default memo(StockPriceChart);
